import React from 'react';
import { useSelector } from 'react-redux';
import { Navigate, useLocation } from 'react-router-dom';
import { tokenService } from '../../../services/tokenService';
import type { RootState } from '../../../store/store';
import { useAuth } from '../AuthProvider';
import { rememberAuthRedirectTarget } from '../authRedirect';
import AuthLoadingScreen from './AuthLoadingScreen';

type AuthenticatedRouteProps = {
  children: React.ReactNode;
  loadingTestId?: string;
};

// Guards authenticated routes against unauthenticated access, and proactively
// rehydrates/refreshes access tokens to avoid inconsistencies on deep links.
export default function AuthenticatedRoute({
  children,
  loadingTestId = 'authenticated-route-loading-screen',
}: AuthenticatedRouteProps): React.ReactElement {
  const location = useLocation();
  const { isAuthenticated, token, status, isUserResolved } = useSelector((s: RootState) => s.auth);
  const { isExchanging } = useAuth();
  const [hydrationComplete, setHydrationComplete] = React.useState(false);
  const [hydrationAttempted, setHydrationAttempted] = React.useState(false);

  React.useEffect(() => {
    let cancelled = false;
    const ensure = async () => {
      if (isAuthenticated && token) {
        if (!cancelled) {
          setHydrationComplete(true);
          setHydrationAttempted(true);
        }
        return;
      }

      if (status === 'expired') {
        if (!cancelled) {
          setHydrationComplete(true);
          setHydrationAttempted(true);
        }
        return;
      }

      try {
        const hydrated = await tokenService.hydrateFromStorage();
        if (!cancelled) {
          setHydrationAttempted(true);
          if (hydrated) {
            setTimeout(() => {
              if (!cancelled) {
                setHydrationComplete(true);
              }
            }, 0);
          } else {
            setHydrationComplete(true);
          }
        }
      } catch (error) {
        console.error('Auth hydration failed:', error);
        if (!cancelled) {
          setHydrationAttempted(true);
          setHydrationComplete(true);
        }
      }
    };

    void ensure();

    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, token, status]);

  React.useEffect(() => {
    if (isAuthenticated && token && hydrationAttempted) {
      setHydrationComplete(true);
    }
  }, [isAuthenticated, token, hydrationAttempted]);

  const isWaitingForAuth =
    isExchanging ||
    !hydrationComplete ||
    (!isAuthenticated && !hydrationAttempted) ||
    (isAuthenticated && !!token && !isUserResolved);
  if (isWaitingForAuth) {
    return <AuthLoadingScreen overlay testId={loadingTestId} />;
  }

  if (!isAuthenticated || !token) {
    const intendedPath = location.pathname + location.search;
    rememberAuthRedirectTarget(intendedPath);
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return <>{children}</>;
}
